import { search as searchRecentSearchesDefault } from '@algolia/autocomplete-plugin-recent-searches'
import { isEmpty, orderBy, uniqBy } from 'lodash-es'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { SearchResultsRoutePathsEnum } from 'Modules/searchResults/routes/searchResultsRouteConfig.types'

import {
  dashOrSpaceToUnderscore,
  fetchFromApiUsingFullPath,
  handleFetchErrorFunctional,
  mergeToQueryString,
} from 'Utilities'

import { SentryErrorType } from 'Constants/errors'
import { PORTAL_CONTENT_MODEL } from 'Constants/portalContentModel'

import { ContentTypeEnum } from 'Types/content/content.types'
import type {
  SearchAutocompleteResults,
  SearchResultDocumentKey,
} from 'Types/globalSearch/searchResults.types'

import type {
  CaseItemStatus,
  CaseItemType,
} from 'Portal/__generated__/globalTypes'
import { getApiConfig } from 'Portal/src/config/api.config'
import { composePath } from 'Portal/src/utilities/composePath/composePath.util'
import { stripHTMLTagsFromString } from 'Portal/src/utilities/stripHTMLTagsFromString/stripHTMLTagsFromString'

import {
  GLOBAL_SEARCH_MIN_QUERY_LENGTH,
  GS_AUTOCOMPLETE_DIRECT_FIELDS,
  GS_AUTOCOMPLETE_TAG_FIELDS,
} from './GlobalSearch.constants'
import type {
  GlobalSearchAutocompleteCategory,
  GlobalSearchAutocompleteInitialiseSource,
  GlobalSearchAutocompleteSource,
  GlobalSearchAutocompleteSuggestion,
  GlobalSearchAutocompleteSuggestionParsedDisplayOrder,
  GlobalSearchAutocompleteSuggestionParsedDisplayReturnCase,
  GlobalSearchAutocompleteTemplateBaseData,
  GlobalSearchAutocompleteTemplateItemData,
  GlobalSearchSubmissionHighlight,
  GlobalSearchSubmissionItem,
  InitialiseSourceConfigurationFn,
  InvalidSourceConfiguration,
  SourceConfigurationFn,
  ValidSourceConfiguration,
} from './GlobalSearch.types'

export const searchRecentSearches = ({
  items,
  limit,
  query,
}: Parameters<typeof searchRecentSearchesDefault>[0]) => {
  const searchableItems = items.filter(
    ({ label }) => label.trim().length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH,
  )
  const uniqueItems = uniqBy(searchableItems, ({ label }) =>
    label.trim().toLowerCase(),
  )

  return searchRecentSearchesDefault({
    items: uniqueItems,
    limit,
    query: query.trim(),
  })
}

const filterUnwantedModels = (rawResponse: SearchAutocompleteResults) =>
  rawResponse.result.filter(
    resultItem =>
      resultItem.model !== ContentTypeEnum.stocklist_items &&
      resultItem.model !== ContentTypeEnum.invoices,
  )

export const stripHtmlFromString = (value: string) =>
  value.replaceAll(/(<([^>]+)>)/g, '')

export const isAutocompleteTagHighlight = (highlight: { field: string }) =>
  GS_AUTOCOMPLETE_TAG_FIELDS.includes(highlight.field)

export const getGlobalSearchNavigationTarget = ({
  basePageUrl,
  query,
  routerSearch,
  selectedItem,
}: {
  basePageUrl: string
  query: string
  routerSearch: string
  selectedItem?: GlobalSearchSubmissionItem
}) => {
  const isTagMatched = selectedItem?.highlights.some(isAutocompleteTagHighlight)

  if (selectedItem && (selectedItem.type === 'direct' || isTagMatched)) {
    let selectedSearchTerm = query

    if (!isTagMatched) {
      selectedSearchTerm =
        selectedItem.category === 'caseitems' ||
        selectedItem.category === 'return_cases'
          ? selectedItem.id
          : selectedItem.highlights[0]?.snippet || selectedItem.snippet
    }

    const targetBaseUrl = selectedItem.organisation_id
      ? composePath({
          params: { organisationId: selectedItem.organisation_id },
          path: GLOBAL_ROUTE_PATHS.organisation.dashboard,
        })
      : basePageUrl

    return {
      pathname: `${targetBaseUrl}/${PORTAL_CONTENT_MODEL[selectedItem.category].path}/${selectedItem.id}`,
      search: mergeToQueryString(routerSearch, {
        searchTerm: stripHtmlFromString(selectedSearchTerm),
      }),
    }
  }

  return {
    pathname: `${basePageUrl}/${SearchResultsRoutePathsEnum.SEARCH_RESULTS}`,
    search: mergeToQueryString(routerSearch, {
      searchTerm: stripHtmlFromString(selectedItem?.snippet || query),
    }),
  }
}

export const getHighlightFieldLabel = (field: string): string =>
  GS_AUTOCOMPLETE_DIRECT_FIELDS[field as SearchResultDocumentKey] ??
  field
    .split('.')
    .splice(-1)[0]
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every(item => typeof item === 'number')

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string')

export const isGlobalSearchSubmissionHighlight = (
  highlight: unknown,
): highlight is GlobalSearchSubmissionHighlight =>
  typeof highlight === 'object' &&
  highlight !== null &&
  'field' in highlight &&
  typeof highlight.field === 'string' &&
  (!('indices' in highlight) ||
    highlight.indices === undefined ||
    isNumberArray(highlight.indices)) &&
  'snippet' in highlight &&
  typeof highlight.snippet === 'string' &&
  (!('snippets' in highlight) ||
    highlight.snippets === undefined ||
    isStringArray(highlight.snippets))

/** API results and recent searches share this submission path. */
export const isGlobalSearchSubmissionItem = (
  item: unknown,
): item is GlobalSearchSubmissionItem =>
  typeof item === 'object' &&
  item !== null &&
  'category' in item &&
  typeof item.category === 'string' &&
  'highlights' in item &&
  Array.isArray(item.highlights) &&
  item.highlights.every(isGlobalSearchSubmissionHighlight) &&
  'id' in item &&
  typeof item.id === 'string' &&
  'snippet' in item &&
  typeof item.snippet === 'string' &&
  'type' in item &&
  (item.type === 'direct' || item.type === 'suggestion') &&
  (!('organisation_id' in item) ||
    item.organisation_id === undefined ||
    item.organisation_id === null ||
    typeof item.organisation_id === 'number')

const mapResponseToAutocompleteCategories = (
  rawResponse: SearchAutocompleteResults,
): GlobalSearchAutocompleteCategory[] | [] => {
  if (isEmpty(rawResponse.result)) {
    return []
  }

  const filteredResult = filterUnwantedModels(rawResponse)

  const categories = filteredResult
    .map<GlobalSearchAutocompleteCategory>(filteredResultItem => ({
      name: filteredResultItem.model,
      resultNo: filteredResultItem.count,
    }))
    .filter(categoryItem => categoryItem.resultNo > 0)

  return categories
}

const mapResponseToAutocompleteSuggestions = (
  rawResponse: SearchAutocompleteResults,
): GlobalSearchAutocompleteSuggestion[] | [] => {
  if (isEmpty(rawResponse.result)) {
    return []
  }

  const filteredResult = filterUnwantedModels(rawResponse)

  const combinedSearchHits = filteredResult.flatMap(
    filteredResultItem => filteredResultItem.hits || [],
  )

  const orderedSearchHits = orderBy(
    combinedSearchHits,
    ['text_match'],
    ['desc'],
  ).slice(0, 12)

  const suggestions = orderedSearchHits.map<GlobalSearchAutocompleteSuggestion>(
    orderedSearchHitsItem => ({
      category: orderedSearchHitsItem.category,
      display: orderedSearchHitsItem.display,
      highlights: orderedSearchHitsItem.highlights ?? [],
      id: orderedSearchHitsItem.entity_id,
      organisation_id: orderedSearchHitsItem.organisation_id,
      organisation_name: orderedSearchHitsItem.organisation_name,
      snippet: orderedSearchHitsItem.display,
      type: orderedSearchHitsItem.type,
    }),
  )

  return suggestions
}

const invalidSource = (
  sourceId: string,
  options: GlobalSearchAutocompleteInitialiseSource,
): InvalidSourceConfiguration[] => [
  {
    getItems() {
      return []
    },
    sourceId,
    templates: {
      noResults(
        noResultsTemplateData: GlobalSearchAutocompleteTemplateBaseData,
      ) {
        return options.renderTemplateNoResults(
          noResultsTemplateData.state.status,
          noResultsTemplateData.state.query,
        )
      },
    },
  },
]

const validSource = (
  sourceId: string,
  response: SearchAutocompleteResults,
  options: GlobalSearchAutocompleteInitialiseSource,
): ValidSourceConfiguration[] => [
  {
    getItemInputValue(
      itemTemplateData: GlobalSearchAutocompleteTemplateItemData,
    ) {
      if (
        itemTemplateData.item.type === 'direct' ||
        itemTemplateData.item.highlights.some(isAutocompleteTagHighlight)
      ) {
        return itemTemplateData.state.query
      }

      return stripHtmlFromString(itemTemplateData.item.snippet)
    },
    getItems() {
      return mapResponseToAutocompleteSuggestions(response)
    },
    onSelect(itemTemplateData: GlobalSearchAutocompleteTemplateItemData) {
      const { event, item } = itemTemplateData

      if (
        event instanceof KeyboardEvent &&
        event.type === 'keydown' &&
        event.code === 'Enter'
      ) {
        options.onKeyboardSelect?.(item, itemTemplateData.state.query.trim())
      }

      if (options.onSelectHandler) {
        options.onSelectHandler()
      }
    },
    sourceId,
    templates: {
      footer(footerTemplateData: GlobalSearchAutocompleteTemplateBaseData) {
        return options.renderTemplateFooter(
          footerTemplateData.state.context.categories,
          footerTemplateData.state.query,
        )
      },
      header(headerTemplateData: GlobalSearchAutocompleteTemplateBaseData) {
        if (isEmpty(headerTemplateData.items)) {
          return null
        }

        return options.renderTemplateHeader()
      },
      item(itemTemplateData: GlobalSearchAutocompleteTemplateItemData) {
        return options.renderTemplateItem(itemTemplateData)
      },
      noResults(
        noResultsTemplateData: GlobalSearchAutocompleteTemplateBaseData,
      ) {
        return options.renderTemplateNoResults(
          noResultsTemplateData.state.status,
          noResultsTemplateData.state.query,
        )
      },
    },
  },
]

export const initialiseSources: InitialiseSourceConfigurationFn =
  (options: GlobalSearchAutocompleteInitialiseSource): SourceConfigurationFn =>
  (sourceProps: GlobalSearchAutocompleteSource) => {
    const { query, setContext } = sourceProps
    const trimmedQuery = query.trim()
    const sourceId = 'gSearch'
    const { globalSearch } = getApiConfig()
    const requestBody = JSON.stringify({
      active_orgId: options.activeOrganisationId,
      model: options.searchModel,
      query: trimmedQuery,
    })

    if (trimmedQuery.length < GLOBAL_SEARCH_MIN_QUERY_LENGTH) {
      return invalidSource(sourceId, options)
    }

    return fetchFromApiUsingFullPath(globalSearch.autocompleteUrl, {
      body: requestBody,
      headers: {
        authorization: options.token,
      },
      method: 'POST',
    })
      .then((response: SearchAutocompleteResults) => {
        setContext({
          categories: mapResponseToAutocompleteCategories(response),
        })

        return validSource(sourceId, response, options)
      })
      .catch(error => {
        handleFetchErrorFunctional()(error, {
          apiUrl: globalSearch.autocompleteUrl,
          location: window?.location || undefined,
          type: SentryErrorType.API_ERROR,
        })

        return invalidSource(sourceId, options)
      })
  }

/**
 * parseOrderDisplay
 *
 * @param display - format: caseItemId|caseItemType|deviceName|caseItemStatus
 */
export function parseOrderDisplay(display: string) {
  return display
    .split('<SEP>')
    .reduce<GlobalSearchAutocompleteSuggestionParsedDisplayOrder>(
      (result, current, index) => {
        switch (index) {
          case 0:
            result.highlight = current
            break
          case 1:
            result.type = current as CaseItemType
            break
          case 2:
            result.name = current
            break
          case 3:
            result.status = dashOrSpaceToUnderscore(
              stripHTMLTagsFromString(current) as CaseItemStatus,
            )
            break
          default:
        }

        return result
      },
      {} as GlobalSearchAutocompleteSuggestionParsedDisplayOrder,
    )
}

/**
 * parseReturnCaseDisplay
 *
 * @param display - format: returnCaseId|deviceName|status
 */
export function parseReturnCaseDisplay(display: string) {
  return display
    .split('<SEP>')
    .reduce<GlobalSearchAutocompleteSuggestionParsedDisplayReturnCase>(
      (result, current, index) => {
        switch (index) {
          case 0:
            result.highlight = current
            break
          case 1:
            result.name = current
            break
          case 2:
            result.portalStatus = stripHTMLTagsFromString(current)
            break
          default:
        }

        return result
      },
      {} as GlobalSearchAutocompleteSuggestionParsedDisplayReturnCase,
    )
}
